import { cx } from '@flowus/common/cx';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import dayjs from 'dayjs';
import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { Button } from 'src/common/components/button';
import { DATE_FORMAT } from 'src/common/const';
import { useModel } from 'src/common/create-model';
import { useCurrentSpace } from 'src/hooks/space';
import { SpacePlanType } from 'src/redux/types';
import { useLimitConfig } from 'src/services/app/hook/subscription-data';
import { useCurrentSpacePlanInfo } from 'src/services/capacity/space-plans';
import { getSpacePlanTypeName } from 'src/utils/block-utils';
import { SettingModalCommon, SettingProvider } from '../common';
import { Option } from '../option';
import { OpenSettingFrom, SettingMenuType } from '../type';
import { useOpenUpgradePlan } from '../use-open-upgrade-plan';

const Tag: FC<{
  planType?: string;
  color?: 'purple' | 'green' | 'black' | 'pink';
  right?: ReactNode;
}> = ({ planType, color, right }) => (
  <div className="flex items-center">
    <div
      className={cx(
        'text-t3-medium py-px px-1.5 mr-2 rounded text-white',
        !color && 'text-grey3-base bg-[#A2A4A633]',
        color === 'green' && 'bg-green',
        color === 'purple' && 'bg-purple',
        color === 'pink' && 'bg-pink_purple',
        color === 'black' && ' bg-[#3D3D3D]'
      )}
    >
      {getSpacePlanTypeName(planType)}
    </div>
    <div className="text-t3 text-grey3">{right}</div>
  </div>
);

export const SpaceInfoPlan: FC = () => {
  const { t } = useAppTranslation();
  const { spacePlan } = useCurrentSpacePlanInfo();
  const { setCurrentSettingMenu, setIsFrom } = useModel(SettingProvider);
  const currentSpace = useCurrentSpace();
  const openUpgradePlan = useOpenUpgradePlan();
  // const { isNovice } = useSpaceCapacity();
  // const openNewUserTask = useOpenNewUserTask();
  const maxBlockLimit = useLimitConfig();

  const [leftChild, rightChild] = useMemo(() => {
    const dayFc = () => {
      const days = dayjs(spacePlan?.endAt).diff(dayjs(), 'day');
      return (
        <>
          {t('components.space_info_setting.space_info_plan.valid_until')}{' '}
          {dayjs(spacePlan?.endAt).format(DATE_FORMAT)},
          <span className={days <= 5 ? 'text-red' : ''}>
            {t('components.space_info_setting.space_info_plan.days_remaining', { days })}
          </span>
        </>
      );
    };

    // 新手，个人
    // if (isNovice) {
    //   return [
    //     <Tag planType={currentSpace.planType} right="新手期，做任务解锁2G容量" />,
    //     <Button className="text-active_color" onClick={openNewUserTask}>
    //       解锁免费扩容
    //     </Button>,
    //   ];
    // }

    // 免费个人
    if (currentSpace.planType === SpacePlanType.freePersonal) {
      return [
        <Tag
          planType={currentSpace.planType}
          right={
            maxBlockLimit.blocksMaxLimit === Infinity
              ? undefined
              : t('components.space_info_setting.space_info_plan.blocks_limit', {
                  limit: maxBlockLimit.blocksMaxLimit,
                  remaining: maxBlockLimit.blocksStock,
                })
          }
        />,
        <Button
          onClick={() => {
            setCurrentSettingMenu(SettingMenuType.upgrade);
            setIsFrom(OpenSettingFrom.dashboard_plan);
          }}
        >
          {t('components.space_info_setting.space_info_plan.upgrade_for_more')}
        </Button>,
      ];
    }

    // 个人专业版
    if (currentSpace.planType === 'personal') {
      return [
        <Tag
          color="green"
          planType={currentSpace.planType}
          right={
            <>
              {dayFc()}
              <span
                hidden={__PRIVATE__}
                className="text-active_color ml-2 animate-click"
                onClick={() => openUpgradePlan(SpacePlanType.pro, OpenSettingFrom.dashboard_plan)}
              >
                {t('components.space_info_setting.space_info_plan.renew_now')}
              </span>
            </>
          }
        />,
      ];
    }

    // 免费小组
    if (currentSpace.planType === 'freeTeam') {
      return [
        <Tag
          planType={currentSpace.planType}
          color="pink"
          right={t('components.space_info_setting.space_info_plan.blocks_limit', {
            limit: maxBlockLimit.blocksMaxLimit,
            remaining: maxBlockLimit.blocksStock,
          })}
        />,
        <Button
          onClick={() => {
            setCurrentSettingMenu(SettingMenuType.upgrade);
            setIsFrom(OpenSettingFrom.dashboard_plan);
          }}
        >
          {t('components.space_info_setting.space_info_plan.upgrade_for_more')}
        </Button>,
      ];
    }

    // 小组版
    if (currentSpace.planType === SpacePlanType.smallTeam) {
      return [
        <Tag
          color="purple"
          planType={currentSpace.planType}
          right={
            <>
              {dayFc()}
              <span
                className="text-active_color ml-2 animate-click"
                onClick={() =>
                  openUpgradePlan(SpacePlanType.smallTeam, OpenSettingFrom.dashboard_plan)
                }
              >
                {t('components.space_info_setting.space_info_plan.renew_now')}
              </span>
            </>
          }
        />,
      ];
    }

    // 团队版
    if (currentSpace.planType === SpacePlanType.team) {
      return [
        <Tag
          color="black"
          planType={currentSpace.planType}
          right={
            <>
              {dayFc()}
              <span
                hidden={__PRIVATE__}
                className="text-active_color ml-2 animate-click"
                onClick={() => openUpgradePlan(SpacePlanType.team, OpenSettingFrom.dashboard_plan)}
              >
                {t('components.space_info_setting.space_info_plan.renew_now')}
              </span>
            </>
          }
        />,
      ];
    }

    return [];
  }, [
    currentSpace.planType,
    spacePlan?.endAt,
    setCurrentSettingMenu,
    setIsFrom,
    openUpgradePlan,
    maxBlockLimit.blocksMaxLimit,
    maxBlockLimit.blocksStock,
    t,
  ]);

  return (
    <>
      <Option title={t('components.space_info_setting.space_info_plan.title')} />
      <SettingModalCommon.Item leftChild={leftChild} rightChild={rightChild} />
    </>
  );
};
